import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Bell, MessagesSquare } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sp_token")}` });

export default function NotificationsBell() {
  const { tr } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = () => axios.get(`${API}/notifications`, { headers: authHeaders() })
      .then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  if (!user) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread > 0) {
      axios.post(`${API}/notifications/mark-read`, {}, { headers: authHeaders() })
        .then(() => setData((d) => ({ ...d, unread: 0 })))
        .catch(() => {});
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notifications-bell" onClick={toggle}
        className="relative p-2 text-[#334155] hover:text-[#2563EB] rounded-lg hover:bg-[#EAF2FD] transition-colors">
        <Bell size={17} />
        {data.unread > 0 && (
          <span data-testid="notifications-unread-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 rounded-full bg-[#DC2626] text-white text-[0.6rem] font-extrabold flex items-center justify-center">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notifications-dropdown" className="absolute right-0 mt-2 w-80 bg-white border border-[#E5EDF7] rounded-2xl shadow-xl overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-[#EEF3FA] font-extrabold text-sm text-[#0F2557]">{tr("Notifications")}</div>
          <div className="max-h-80 overflow-y-auto">
            {data.items.length === 0 && (
              <p className="px-4 py-6 text-sm text-[#64748B]">{tr("Follow places to get notified when their community boards light up.")}</p>
            )}
            {data.items.map((n) => (
              <a key={n.id} href={`${n.url}#community`} data-testid="notification-item"
                className={`block px-4 py-3 border-b border-[#F3F6FB] hover:bg-[#F6FAFF] transition-colors ${!n.read ? "bg-[#F5F3FF]" : ""}`}>
                <div className="flex items-center gap-2 text-xs font-extrabold text-[#0F2557]">
                  <MessagesSquare size={12} className="text-[#7C3AED]" />
                  {n.flag} {n.place_name}
                  <span className="ml-auto font-semibold text-[0.6rem] text-[#94A3B8]">{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-xs text-[#475569] mt-1 line-clamp-2">{n.author}: "{n.snippet}"</p>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
